import { Fragment, type ReactNode } from "react";
import { Link, useMatches } from "react-router-dom";

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@krea/shared/design-system/breadcrumb";

interface Crumb {
  label: ReactNode;
  to?: string;
}

type CrumbFn = (args: {
  params: Record<string, string | undefined>;
  pathname: string;
}) => Crumb[] | null;

export const Breadcrumbs = () => {
  const matches = useMatches();

  const crumbs: Crumb[] = matches
    .flatMap((m) => {
      const fn =
        m.handle && typeof m.handle === "object" && "crumbs" in m.handle
          ? (m.handle.crumbs as CrumbFn)
          : undefined;

      return fn ? fn({ params: m.params, pathname: m.pathname }) : null;
    })
    .filter((c): c is Crumb => Boolean(c));
  if (crumbs.length === 0) return null;

  return (
    <Breadcrumb>
      <BreadcrumbList>
        {crumbs.map((crumb, index) => (
          <Fragment key={index}>
            <BreadcrumbItem key={index}>
              {crumb.to ? (
                <BreadcrumbLink asChild>
                  <Link to={crumb.to}>{crumb.label}</Link>
                </BreadcrumbLink>
              ) : (
                <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
              )}
            </BreadcrumbItem>
            {index < crumbs.length - 1 && (
              <BreadcrumbSeparator className="hidden md:block" />
            )}
          </Fragment>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  );
};
